import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { AttackStatus } from '../../attack-status'
import { DrilldownHeader } from './drilldown-header'
import { DrilldownActions } from './drilldown-actions'
import { CsAttackIcon } from '../../../legacy-design-system/icons'
import { AttackEventOverview } from './overview'
import {
  HttpRequest,
  AttackEventDetails,
  Application,
  Server,
  UserInput,
  StackFrame
} from '../../attack-event/shared'
import { AttackEventRequest } from './request/attack-event-request'
import { AttackEventDiscussion } from './discussion/attack-event-discussion'
import { dispatchUpdateAttackEventNotesViewed } from './discussion/actions/update-attack-event-notes-viewed-mutation'
import { ApolloConsumer } from 'react-apollo'
import { AttackEventDetail } from '../../attack-event/attack-event-details'
import { GridDrilldownPanelViewWithTabs } from '../../../legacy-design-system/grid-drilldown-panel/grid-drilldown-panel-view-with-tabs'

enum AttackEventDrilldownTabs {
  OVERVIEW = 'overview',
  DETAILS = 'details',
  REQUEST = 'request',
  DISCUSSION = 'discussion'
}

export interface Chapter {
  readonly type: string
  readonly introText?: string
  readonly introTemplate?: string
  readonly introVariables?: {
    readonly [key: string]: any
  }
  readonly body?: string
  readonly bodyTemplate?: string
  readonly bodyVariables?: {
    readonly [key: string]: any
  }
}

export interface DrilldownAttackEvent {
  readonly id: string
  readonly ruleUuid: string
  readonly type: string
  readonly displayName: string
  readonly sourceIP?: string
  readonly sourceName?: string
  readonly attackers: string[]
  readonly result: AttackStatus
  readonly received: string
  readonly application: Application
  readonly url?: string
  readonly server: Server & {
    readonly environment: string
  }
  readonly httpRequest?: HttpRequest
  readonly file?: string
  readonly method?: string
  readonly line?: number
  readonly stackFrames?: StackFrame[]
  readonly userInput?: UserInput
  readonly details?: AttackEventDetails
  readonly detectionType?: string
  readonly callLocation?: string
  readonly tags: string[]
  readonly storyChapters: Chapter[]
  readonly requestChapters?: Chapter[]
  readonly hidden: boolean
  readonly lastCustomFrame?: number
  readonly signature?: string
  readonly vector?: string
}

interface DrilldownContentViewProps {
  readonly layout: 'panel' | 'page'
  readonly onRemoveTag: (tag: string) => void
  readonly organizationId: string
  readonly attackEvent: DrilldownAttackEvent
}

export function DrilldownContentView({
  layout,
  onRemoveTag,
  organizationId,
  attackEvent
}: DrilldownContentViewProps) {
  const [discussionCount, setDiscussionCount] = useState(0)

  return (
    <ApolloConsumer>
      {(apolloClient) => (
        <GridDrilldownPanelViewWithTabs
          layout={layout}
          defaultTabID={AttackEventDrilldownTabs.OVERVIEW}
          renderIcon={({ defaultIconProps }) => (
            <CsAttackIcon {...defaultIconProps} />
          )}
          renderHeader={() => (
            <DrilldownHeader
              showWhiteBackgroundColor={layout === 'page'}
              onRemoveTag={onRemoveTag}
              organizationId={organizationId}
              attackEvent={attackEvent}
              renderActions={<DrilldownActions />}
            />
          )}
          tabHeadingConfigs={[
            {
              id: AttackEventDrilldownTabs.OVERVIEW,
              content: () => <FormattedMessage id="title_overview" />
            },
            {
              id: AttackEventDrilldownTabs.DETAILS,
              content: () => <FormattedMessage id="title_details" />
            },
            {
              id: AttackEventDrilldownTabs.REQUEST,
              content: () => <FormattedMessage id="title_request" />,
              hide: !attackEvent.httpRequest
            },
            {
              id: AttackEventDrilldownTabs.DISCUSSION,
              content: () => (
                <FormattedMessage
                  id="title_discussionWithCount"
                  values={{ count: discussionCount }}
                />
              ),
              onInactiveTabClick: () => {
                dispatchUpdateAttackEventNotesViewed(
                  apolloClient,
                  organizationId,
                  attackEvent.id
                )
              }
            }
          ]}
          renderActions={() => <DrilldownActions />}
          tabContentConfigs={[
            {
              id: AttackEventDrilldownTabs.OVERVIEW,
              content: () => (
                <AttackEventOverview
                  organizationID={organizationId}
                  attackEvent={attackEvent}
                />
              )
            },
            {
              id: AttackEventDrilldownTabs.DETAILS,
              content: () => (
                <AttackEventDetail
                  organizationId={organizationId}
                  attackEvent={attackEvent}
                />
              )
            },
            {
              id: AttackEventDrilldownTabs.REQUEST,
              content: () => <AttackEventRequest attackEvent={attackEvent} />
            },
            {
              id: AttackEventDrilldownTabs.DISCUSSION,
              content: () => (
                <AttackEventDiscussion
                  organizationId={organizationId}
                  attackEventUuid={attackEvent.id}
                  refreshDiscussionCount={setDiscussionCount}
                />
              )
            }
          ]}
        />
      )}
    </ApolloConsumer>
  )
}
